import React, { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandle } from '@mui/icons-material';
import { MenuItem, Switch } from '@mui/material';
import { EllipsisVertical, Pencil, Trash2 } from 'lucide-react';
import numbro from 'numbro';

import { type TypeScenarioVector } from '../../common/dataTypes';
import { useGetProjectById, useUpdateVector } from '../../hooks';
import { useGetScenarioVectors } from '../../hooks/tanstack/useScenariosHook';
import {
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import {
  useDeleteVector,
  useUpdateVectorOrder,
} from '../../hooks/tanstack/useVectorHook';
import cn from '../../utils/cn';
import LoadingDialog from '../LoadingDialog';
import MenuDropdown from '../MenuDropdown';
import SimpleConfirmDeleteDialog from '../SimpleConfirmDeleteDialog';
import CreateVectorFormDialog, {
  VECTOR_DIRECTION_DECREASE,
  VECTOR_DIRECTION_INCREASE,
  VECTOR_DIRECTION_LOWER_BOUND,
  VECTOR_DIRECTION_SET,
  VECTOR_DIRECTION_UPPER_BOUND,
  VECTOR_TYPE_DELTA,
  VECTOR_TYPE_PERCENTAGE,
} from '../vector/CreateVectorFormDialog';

const getVectorDirection = (vector: TypeScenarioVector) => {
  if (
    (vector.delta && vector.delta > 0) ||
    (vector.delta_percentage && vector.delta_percentage > 0)
  ) {
    return VECTOR_DIRECTION_INCREASE;
  }

  if (
    (vector.delta && vector.delta < 0) ||
    (vector.delta_percentage && vector.delta_percentage < 0)
  ) {
    return VECTOR_DIRECTION_DECREASE;
  }

  if (vector.new_value) {
    return VECTOR_DIRECTION_SET;
  } else if (vector.upper_bound) {
    return VECTOR_DIRECTION_UPPER_BOUND;
  } else if (vector.lower_bound) {
    return VECTOR_DIRECTION_LOWER_BOUND;
  }
};

const DraggableVector = ({
  vector,
  handleOnActivate,
  isEditable,
}: {
  isEditable: boolean;
  vector: TypeScenarioVector;
  handleOnActivate?: () => void;
}) => {
  const [checked, setChecked] = useState(vector.is_active);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [openLoadingDialog, setOpenLoadingDialog] = useState(false);

  const [openDialogCreateVector, setOpenDialogCreateVector] = useState(false);

  const open = Boolean(anchorEl);

  const { transform, transition, setNodeRef, attributes, listeners } =
    useSortable({
      id: vector.id,
    });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
  };

  const [searchParams] = useSearchParams();
  const scenarioId = Number(searchParams.get('scenario_id')) ?? NaN;

  const { projectId, modelId, orgId } = useGetRouteParams();

  const { model } = useGetDistinguishModel();

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const { mutate: updateVector } = useUpdateVector({
    onSuccess() {
      handleOnActivate?.();
      setOpenLoadingDialog(false);
    },
  });

  const { mutate: deleteVector, isPending: isDeletingVector } = useDeleteVector(
    {
      onSuccess() {
        handleOnActivate?.();
        handleCloseConfirmDelete();
      },
    }
  );

  const direction = getVectorDirection(vector);

  const getCriteriaValue = (field_name: string) =>
    vector?.criterias?.find((c) => c.field_name === field_name)?.value;

  const aircraftTypeValue = useMemo(
    () => getCriteriaValue('aircraft_type'),
    [vector?.criterias]
  );

  const airportCodeValue = useMemo(
    () => getCriteriaValue('airport_code'),
    [vector?.criterias]
  );

  const currentPositionValue = useMemo(
    () => getCriteriaValue('current_position'),
    [vector?.criterias]
  );

  const crewType = useMemo(
    () => getCriteriaValue('crew_type'),
    [vector?.criterias]
  );

  const trainingPositionValue = useMemo(
    () => getCriteriaValue('training_position'),
    [vector?.criterias]
  );

  const handleCloseDropdown = () => setAnchorEl(null);

  const handleOpenDialogCreateVector = () => setOpenDialogCreateVector(true);

  const handleToggleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;

    setChecked(value);

    setOpenLoadingDialog(true);

    if (value) {
      updateVector({
        orgId,
        vectorId: vector.id,
        projectId,
        scenarioId,
        modelId,
        data: {
          ...vector,
          scenario: scenarioId,
          is_active: true,
        },
      });
    } else {
      updateVector({
        orgId,
        vectorId: vector.id,
        projectId,
        scenarioId,
        modelId,
        data: {
          ...vector,
          scenario: scenarioId,
          is_active: false,
        },
      });
    }
  };

  const handleCloseConfirmDelete = () => {
    setOpenConfirmDelete(false);
  };

  const handleDelete = () => {
    if (orgId && vector && projectId) {
      deleteVector({
        orgId,
        projectId,
        modelId,
        scenarioId,
        vectorId: vector.id,
      });
    }
  };

  const defaultValues = useMemo(() => {
    const result = {
      id: vector.id,
      name: vector.name,
      description: vector.description,
      start_period: vector.start_period,
      end_period: vector.end_period,
      period: vector.period,
      type:
        vector.new_value || vector.delta
          ? VECTOR_TYPE_DELTA
          : VECTOR_TYPE_PERCENTAGE,
      new_value: vector.new_value,
      action:
        vector.new_value && !vector.delta && !vector.delta_percentage
          ? VECTOR_DIRECTION_SET
          : (vector.delta
                ? (vector.delta as number)
                : (vector.delta_percentage as number)) > 0
            ? VECTOR_DIRECTION_INCREASE
            : vector.upper_bound
              ? VECTOR_DIRECTION_UPPER_BOUND
              : vector.lower_bound
                ? VECTOR_DIRECTION_LOWER_BOUND
                : VECTOR_DIRECTION_DECREASE,
      amount:
        (vector.new_value ?? vector.upper_bound ?? vector.lower_bound) &&
        !vector.delta &&
        !vector.delta_percentage
          ? Math.abs(
              vector.new_value ??
                vector.upper_bound ??
                (vector.lower_bound as number)
            )
          : vector.delta
            ? Math.abs(vector.delta)
            : Math.abs(vector.delta_percentage as number),
    } as Record<string, any>;

    vector.criterias.forEach((c) => {
      result[c.field_name] = c.value;
    });

    return result;
  }, [vector]);

  return (
    <div
      ref={project?.is_guest_project ? null : setNodeRef}
      className="flex gap-4 items-center"
      style={style}
    >
      {!project?.is_guest_project && (
        <button type="button" {...attributes} {...listeners}>
          <DragHandle
            style={{
              color: '#666',
            }}
          />
        </button>
      )}
      <div className="flex flex-col w-full gap-3 bg-white rounded-[4px] border border-[#E4E7EC] p-5">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <p className="font-bold">{vector.name}</p>
          </div>
          {!model.is_locked && isEditable && (
            <div className="flex items-center gap-3">
              <Switch
                checked={checked}
                onChange={(e) => handleToggleCheck(e)}
                inputProps={{ 'aria-label': 'controlled' }}
              />
              <button
                type="button"
                onClick={() => {
                  handleCloseDropdown();
                  handleOpenDialogCreateVector();
                }}
              >
                <Pencil size={20} color="#666666" />
              </button>
              <MenuDropdown
                trigger={
                  <button className="w-fit" type="button">
                    <EllipsisVertical color="#666666" width={20} height={20} />
                  </button>
                }
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                style={{
                  marginTop: '5px',
                  marginLeft: '-5px',
                }}
                open={open}
              >
                <div className="!border-t !border-[#E4E7EC]" />
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    setOpenConfirmDelete(true);
                  }}
                >
                  <div className="flex items-center space-x-2 text-primary-red">
                    <Trash2 size={15} />
                    <p>Delete</p>
                  </div>
                </MenuItem>
              </MenuDropdown>
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <p>{vector.description}</p>
        </div>
        <div
          className={cn(
            'flex grow text-[#333] flex-wrap transition items-center gap-3 bg-[#FFF] !text-sm',
            {
              '!text-[#B3B3B3]': !checked,
            }
          )}
        >
          <span className={cn('font-bold transition uppercase')}>
            {direction}
          </span>

          {direction === VECTOR_DIRECTION_SET && (
            <p className="space-x-3">
              <span className="font-bold uppercase">to</span>
              <span
                className={cn(
                  'rounded-full bg-[#EFFCFF] text-[#0F95B8] px-3 py-1 font-semibold',
                  {
                    'text-[#B3B3B3] bg-[#F5F5F5]': !checked,
                  }
                )}
              >
                {numbro(vector.new_value).format({
                  thousandSeparated: true,
                  mantissa: 2,
                }) + ' Fixed Amount'}
              </span>
            </p>
          )}

          {(direction === VECTOR_DIRECTION_INCREASE ||
            direction === VECTOR_DIRECTION_DECREASE) && (
            <p className="space-x-3">
              <span className="font-bold uppercase">by</span>
              <span
                className={cn(
                  'rounded-full bg-[#EFFCFF] text-[#0F95B8] px-3 py-1 font-semibold',
                  {
                    'text-[#B3B3B3] bg-[#F5F5F5]': !checked,
                  }
                )}
              >
                {vector.delta
                  ? numbro(vector.delta?.toString().replace('-', '')).format({
                      thousandSeparated: true,
                      mantissa: 2,
                    }) + ' Fixed Amount'
                  : numbro(
                      vector.delta_percentage?.toString().replace('-', '')
                    ).format({
                      thousandSeparated: true,
                      mantissa: 2,
                    }) + '%'}
              </span>
            </p>
          )}

          {(direction === VECTOR_DIRECTION_UPPER_BOUND ||
            direction === VECTOR_DIRECTION_LOWER_BOUND) && (
            <p className="space-x-3">
              <span className="font-bold uppercase">by</span>
              <span
                className={cn(
                  'rounded-full bg-[#EFFCFF] text-[#0F95B8] px-3 py-1 font-semibold',
                  {
                    'text-[#B3B3B3] bg-[#F5F5F5]': !checked,
                  }
                )}
              >
                {vector.upper_bound ?? vector.lower_bound} Fixed Amount
              </span>
            </p>
          )}

          {aircraftTypeValue ? (
            <p className="space-x-3">
              <span className="font-bold uppercase">on</span>
              <>
                <span>aircraft type</span>
                <span>=</span>
                <span
                  className={cn(
                    'rounded-full bg-[#E8F2FF] transition px-3 py-1 font-semibold capitalize text-[#01285F]',
                    {
                      'text-[#B3B3B3] bg-[#F5F5F5]': !checked,
                    }
                  )}
                >
                  {aircraftTypeValue}
                </span>
              </>
            </p>
          ) : null}

          {airportCodeValue ? (
            <p className="space-x-3">
              <span className="font-bold uppercase">AND</span>
              <>
                <span>airport code</span>
                <span>=</span>
                <span
                  className={cn(
                    'rounded-full bg-[#E8F2FF] transition px-3 py-1 font-semibold capitalize text-[#01285F]',
                    {
                      'text-[#B3B3B3] bg-[#F5F5F5]': !checked,
                    }
                  )}
                >
                  {airportCodeValue}
                </span>
              </>
            </p>
          ) : null}

          {crewType ? (
            <p className="space-x-3">
              <span className="font-bold uppercase">AND</span>
              <>
                <span>crew type</span>
                <span>=</span>
                <span
                  className={cn(
                    'rounded-full bg-[#efe6f0] transition px-3 py-1 font-semibold capitalize text-[#8e328a]',
                    {
                      'text-[#B3B3B3] bg-[#F5F5F5]': !checked,
                    }
                  )}
                >
                  {crewType}
                </span>
              </>
            </p>
          ) : null}

          {currentPositionValue ? (
            <p className="space-x-3">
              <span className="font-bold uppercase">AND</span>
              <>
                <span>current position</span>
                <span>=</span>
                <span
                  className={cn(
                    'rounded-full bg-[#E8F2FF] transition px-3 py-1 font-semibold capitalize text-[#01285F]',
                    {
                      'text-[#B3B3B3] bg-[#F5F5F5]': !checked,
                    }
                  )}
                >
                  {currentPositionValue}
                </span>
              </>
            </p>
          ) : null}
          {trainingPositionValue ? (
            <p className="space-x-3">
              <span className="font-bold uppercase">AND</span>
              <>
                <span>training_position</span>
                <span>=</span>
                <span
                  className={cn(
                    'rounded-full bg-[#ecd5d9] transition px-3 py-1 font-semibold capitalize text-[#cf5085]',
                    {
                      'text-[#B3B3B3] bg-[#F5F5F5]': !checked,
                    }
                  )}
                >
                  {trainingPositionValue}
                </span>
              </>
              <span className="font-bold uppercase">AND</span>
            </p>
          ) : null}
          <span>timeline from</span>
          <span
            className={cn(
              'rounded-full bg-[#FFF4F2] px-3 py-1 transition font-semibold capitalize text-primary-red',
              {
                'text-[#B3B3B3] bg-[#F5F5F5]': !checked,
              }
            )}
          >
            {vector?.start_period}
          </span>
          <span>to</span>
          <span
            className={cn(
              'rounded-full bg-[#FFF4F2] px-3 transition py-1 font-semibold capitalize text-primary-red',
              {
                'text-[#B3B3B3] bg-[#F5F5F5]': !checked,
              }
            )}
          >
            {vector?.end_period}
          </span>
        </div>
        <SimpleConfirmDeleteDialog
          open={openConfirmDelete}
          isLoading={isDeletingVector}
          title={`Are you sure you want to delete ${vector.name}?`}
          handleOnClose={handleCloseConfirmDelete}
          handleOnSubmit={handleDelete}
        />
        <CreateVectorFormDialog
          open={openDialogCreateVector}
          handleCloseDialog={() => {
            setOpenDialogCreateVector(false);
          }}
          handleOpenDialog={() => {
            setOpenDialogCreateVector(true);
          }}
          handleSubmitDialog={handleOnActivate}
          title="Edit Vector"
          defaultValues={defaultValues}
          showTrigger={false}
        />
        <LoadingDialog
          title={`${!checked ? 'De-activating' : 'Activating'} "${vector.name}" vector`}
          open={openLoadingDialog}
        />
      </div>
    </div>
  );
};

type VectorSectionProps = {
  handleOnActivate?: () => void;
};

const VectorsSection = ({ handleOnActivate }: VectorSectionProps) => {
  const { projectId, orgId, modelId, scenarioId } = useGetRouteParams();
  const [data, setData] = useState<TypeScenarioVector[]>([]);

  const { data: vectors } = useGetScenarioVectors(
    {
      scenarioId,
      orgId,
      projectId,
      modelId,
    },
    {
      enabled: !!scenarioId && !!orgId && !!projectId && !!modelId,
    }
  );

  const { mutateAsync: updateVectorOrder } = useUpdateVectorOrder();

  const { isDefault } = useGetDistinguishModel();

  const dataIds = React.useMemo<UniqueIdentifier[]>(
    () => data.map(({ id }) => id),
    [data]
  );

  useEffect(() => {
    if (!vectors) {
      return;
    }
    setData(vectors);
  }, [vectors]);

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      setData((data) => {
        const oldIndex = dataIds.indexOf(active.id);
        const newIndex = dataIds.indexOf(over.id);

        const newData = arrayMove(data, oldIndex, newIndex).map(
          (item, index) => ({
            ...item,
            hierarchy: index + 1,
          })
        );

        updateVectorOrder({
          orgId,
          projectId,
          modelId,
          scenarioId,
          data: { vectors: newData.map((v) => v.id) },
        });

        return newData;
      });
    }
  };

  return data.length ? (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div className="flex flex-col gap-3 bg-[#FFF]">
        <SortableContext items={data} strategy={verticalListSortingStrategy}>
          {data.map((vector) => (
            <DraggableVector
              isEditable={isDefault}
              key={vector.id}
              vector={vector}
              handleOnActivate={handleOnActivate}
            />
          ))}
        </SortableContext>
      </div>
    </DndContext>
  ) : (
    <div className="w-full py-12 text-center text-gray-500 border rounded-[4px] border-[#E4E7EC]">
      No Vectors. Start by adding a new Vector.
    </div>
  );
};

export default VectorsSection;
